import { Fragment, useEffect, useState } from 'react';
import { Button } from '../../../../core/components/atoms/Button';
import Container from '../../../../core/components/atoms/Container';
import PageHeader from '../../../../core/components/molecules/Header';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useGetPractices } from '../../hooks/getPractice';
import ContentLoader from 'react-content-loader';
import { PracticeCard } from '../../components/PracticeCard';
import { useNavigate, useParams } from 'react-router-dom';
import { Toast, toast } from '../../../../core/components/atoms/Toast';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Tab } from '@headlessui/react';
import ModalFilterPractices from '../../components/ModalFilter';
import { IPractices } from '../../model';
import { practiceFilterStoreStore } from '../../stores/practiceFilterStore';
import { EmptyState } from '../../components/EmptyState';
import { PracticeFooter } from '../../components/PracticeFooter';
import { ModalHowTo } from '../../components/ModalHowTo';
import { PracticeRevisions } from '../../components/Revisions';
import { MODAL_VIDEOS_VIMEO_ALLOWED } from '../../../../core/constants/modal-videos';
import { Modal } from '../../../../core/components/templates/Modal';
import { useDeleteManyPractices } from '../../hooks/deleteManyPractices';

export function PracticeList() {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { isLoading, data, isError, mutate } = useGetPractices();
  const { modality, rate } = practiceFilterStoreStore();

  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);

  const {
    mutate: deleteMany,
    data: deleteManyData,
    isPending: isDeletingMany,
    isSuccess: isDeleteManySuccess,
  } = useDeleteManyPractices();

  useEffect(() => {
    if (tab) {
      if (tab === 'provas') {
        setActiveTab(0);
      } else {
        setActiveTab(1);
      }
    }

    mutate();
  }, []);

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        message: 'Não foi possivel buscar seus simulados',
        title: 'Erro!',
      });
    }
  }, [isError]);

  const changeTab = (tab: number) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  };

  const submitFilter = () => {
    setFilterOpen(false);
    mutate();
  };

  useEffect(() => {
    if (isDeleteManySuccess && deleteManyData?.ok) {
      setConfirmDeleteOpen(false);
      setSelectionMode(false);
      setSelectedIds([]);
      mutate();
    }
  }, [isDeleteManySuccess]);

  const enterSelectionMode = () => {
    setSelectedIds([]);
    setSelectionMode(true);
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedIds([]);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((it) => it !== id) : [...prev, id]
    );
  };

  const showContent = () => {
    if (!data && !modality && !rate) {
      return (
        <EmptyState
          text='Seu histórico está vazio, pois você ainda não criou simulados.'
          subtext='Seus simulados aparecerão aqui após serem criados.'
        />
      );
    }

    return (
      <div className='flex flex-col gap-xl'>
        {data &&
          data.map((it: IPractices, i: number) => (
            <PracticeCard
              data={it}
              key={i}
              onRefresh={() => mutate()}
              selectionMode={selectionMode}
              selected={selectedIds.includes(it._id)}
              onToggleSelect={toggleSelect}
            />
          ))}
      </div>
    );
  };

  return (
    <Container gap='xxl' direction='col' className='xs:mb-[30px] xl:mb-[30px]'>
      <div className='flex justify-between xs:flex-col xs:items-start md:flex-row md:items-center'>
        <div className='flex-1'>
          <PageHeader title='Simulador de provas' module='simulador' />
        </div>
        {MODAL_VIDEOS_VIMEO_ALLOWED.SIMULADOR.allowed && (
          <Button
            allowReadOnly
            variant='text'
            LeftIcon='info'
            iconType='material'
            iconSize='small'
            className='p-0 text-md md:text-md'
            onClick={() => setIsOpen(true)}
          >
            Como funciona
          </Button>
        )}
      </div>
      <div className='xs:mb-0 md:mb-5 lg:mb-10'>
        {activeTab === 0 && (
          <div
            className={`bg-white mb-8 w-full flex-col items-center gap-xl rounded-md py-4 xs:flex lg:hidden`}
          >
            <Typography weight='bold'>
              Crie quantos simulados quiser!
            </Typography>
            <Button onClick={() => navigate('/simulados/criar')}>
              <Icon Icon='add' type='material' />
              Criar simulado
            </Button>
          </div>
        )}
        <Tab.Group selectedIndex={activeTab} onChange={changeTab}>
          <Tab.List className='m-auto rounded-pill border-hairline border-mono-color-medium bg-color-lightest xs:w-full lg:w-2/4'>
            <Tab
              className={`w-1/2 rounded-l-pill py-2 ${activeTab === 0 && 'bg-color-brand-primary'}`}
            >
              <Typography
                type='title'
                className={`${activeTab === 0 && 'text-button-color-primary-default'} xs:text-sm lg:text-h4`}
              >
                Histórico de simulados
              </Typography>
            </Tab>
            <Tab
              className={`w-1/2 rounded-r-pill py-2 ${activeTab === 1 && 'bg-color-brand-primary'}`}
            >
              <Typography
                type='title'
                className={`${activeTab === 1 && 'text-button-color-primary-default'} xs:text-sm lg:text-h4`}
              >
                Revisões
              </Typography>
            </Tab>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel className='flex flex-col gap-xxl'>
              <Typography type='title' className='mt-8 xs:text-h3 lg:text-h2'>
                Histórico de simulados
              </Typography>
              {selectionMode ? (
                <div className='flex flex-row items-center justify-between gap-md rounded-md bg-color-brand-primary/[0.06] p-3 xs:flex-col xs:items-start md:flex-row md:items-center'>
                  <div className='flex flex-row items-center gap-md'>
                    <Icon
                      Icon='check_box'
                      type='material'
                      className='text-color-brand-primary'
                    />
                    <Typography weight='bold'>
                      {selectedIds.length}{' '}
                      {selectedIds.length === 1
                        ? 'simulado selecionado'
                        : 'simulados selecionados'}
                    </Typography>
                  </div>
                  <div className='flex flex-row items-center gap-sm xs:w-full xs:justify-end md:w-fit'>
                    <Button
                      variant='outlined'
                      className='text-feedback-color-error-pure'
                      disabled={selectedIds.length === 0}
                      onClick={() => setConfirmDeleteOpen(true)}
                    >
                      <Icon Icon='delete' type='material' />
                      Excluir selecionados
                    </Button>
                    <Button variant='outlined' onClick={cancelSelection}>
                      Cancelar seleção
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='flex flex-row items-center justify-between'>
                  <Typography size='small'>
                    {data && data.length} Simulados
                  </Typography>
                  <div className='flex flex-row items-center gap-lg'>
                    {data && data.length > 0 && (
                      <Button variant='outlined' onClick={enterSelectionMode}>
                        <Icon Icon='highlight_alt' type='material' />
                        Selecionar
                      </Button>
                    )}
                    <Button
                      variant='text'
                      onClick={() => setFilterOpen(true)}
                      className='p-0'
                    >
                      <Icon
                        Icon='filter_list'
                        type='material'
                        className='xs:text-sm md:text-md'
                      />
                      <span className='xs:text-sm md:text-md '>
                        Filtrar simulados
                      </span>
                    </Button>
                  </div>
                </div>
              )}
              {isLoading ? (
                <Fragment>
                  {Array.from({ length: 10 }, (_, index) => (
                    <ContentLoader
                      key={index}
                      backgroundColor={'#a7adba'}
                      foregroundColor={'#c0c5ce'}
                      viewBox='0 0 500 35'
                    >
                      <rect
                        x='0'
                        y='10'
                        rx='2'
                        ry='2'
                        width='300'
                        height='13'
                      />
                      <rect
                        x='0'
                        y='25'
                        rx='2'
                        ry='2'
                        width='250'
                        height='10'
                      />
                    </ContentLoader>
                  ))}
                </Fragment>
              ) : (
                showContent()
              )}
            </Tab.Panel>
            <Tab.Panel className='flex flex-col gap-xxl'>
              <PracticeRevisions />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
      <PracticeFooter action={() => navigate('/simulados/criar')} />
      {isError && <Toast />}
      <ModalHowTo isOpen={isOpen} onClose={() => setIsOpen(false)} />
      <ModalFilterPractices
        onClose={() => setFilterOpen(false)}
        onSubmit={() => submitFilter()}
        isOpen={filterOpen}
      />
      <Modal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        position='center'
        className='p-0 xs:w-full lg:w-2/5'
      >
        <div className='flex flex-col gap-xl'>
          <div className='flex flex-col gap-sm'>
            <Typography weight='bold'>
              Tem certeza que deseja excluir{' '}
              {selectedIds.length === 1
                ? 'esse simulado'
                : `esses ${selectedIds.length} simulados`}
              ?
            </Typography>
            <Typography>
              Não será possível recuperar os simulados excluídos.
            </Typography>
          </div>
          <div className='flex flex-row items-center justify-end gap-sm'>
            <Button variant='text' onClick={() => setConfirmDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant='text'
              disabled={isDeletingMany}
              onClick={() => deleteMany(selectedIds)}
            >
              Excluir simulados
            </Button>
          </div>
        </div>
      </Modal>
    </Container>
  );
}
